import React, { useState, useEffect } from 'react';
import {
  string, number, arrayOf, func, bool, shape
} from 'prop-types';
import { Viewport } from './Viewport';
import { getPxInInches } from '../../../../utils/wall-size-utils';
import { Wall } from './Wall';
import { CabinetShape, SegmentShape } from './type';
import { useViewportStore } from '../../../../store/ui';

const DEFAULT_WIDTH = 144;

export const WallVisualizer = React.memo(({
  cabinets, cart, rows, columns, segments, zoom, onTouchPoint, onTouchPointForSpacer,
  onTouchPointForFiller, handleQuickView, handleDuplicate, designId, onRender,
  showDimensions, totalWidth, onEditCabinetAnalytics, disableTouchPoints,
  onFloorRectChange, onWallContainerRefChange, overlayPosition
}) => {

  const viewportInfo = useViewportStore((state) => state.viewportInfo);
  const setViewportInfo = useViewportStore((state) => state.setViewportInfo);

  const [width, setWidth] = useState(0);

  const updateViewportInfo = (newWidth, scrollOffset = { scrollLeft: 0, scrollTop: 0 }) => {
    const finalZoom = onRender ? onRender(newWidth) : zoom;
    const isManual = viewportInfo.manualZoom;
    const newBaseZoom = isManual ? viewportInfo.baseZoom : finalZoom;

    if (viewportInfo.width !== newWidth
      || viewportInfo.finalZoom !== finalZoom
      || viewportInfo.baseZoom !== newBaseZoom
      || viewportInfo.scrollOffset.scrollLeft !== scrollOffset.scrollLeft
      || viewportInfo.scrollOffset.scrollTop !== scrollOffset.scrollTop) {
      setViewportInfo({
        width: newWidth,
        finalZoom,
        scrollOffset,
        manualZoom: isManual,
        baseZoom: newBaseZoom
      });
    }
  };

  useEffect(() => {
    if (width !== 0) {
      updateViewportInfo(width, viewportInfo.scrollOffset);
    }
  }, [width, onRender, zoom]);

  return (
    <div>
      <Viewport
        zoom={zoom}
        onWidthChange={setWidth}
        onScrollChange={(scrollOffset) => updateViewportInfo(width, scrollOffset)}
        render={() => {
          const finalZoom = viewportInfo?.finalZoom ?? (onRender ? onRender(width) : zoom);
          return (
            <Wall
              cabinets={cabinets}
              segments={segments}
              rows={rows}
              columns={columns}
              zoom={finalZoom}
              pxInInches={getPxInInches(width, DEFAULT_WIDTH)}
              onTouchPoint={onTouchPoint}
              onTouchPointForSpacer={onTouchPointForSpacer}
              onTouchPointForFiller={onTouchPointForFiller}
              handleQuickView={handleQuickView}
              handleDuplicate={handleDuplicate}
              designId={designId}
              showDimensions={showDimensions}
              onEditCabinetAnalytics={onEditCabinetAnalytics}
              onFloorRectChange={onFloorRectChange}
              onWallContainerRefChange={onWallContainerRefChange}
              disableTouchPoints={disableTouchPoints}
              scrollOffset={viewportInfo.scrollOffset} // Pass scrollOffset to Wall
              overlayPosition={overlayPosition}
            />
          );
        }}
      />
    </div>
  );
});

WallVisualizer.propTypes = {
  cabinets: arrayOf(CabinetShape).isRequired,
  cart: arrayOf(shape({})),
  rows: number,
  columns: number,
  segments: arrayOf(SegmentShape).isRequired,
  zoom: number,
  onTouchPoint: func,
  onTouchPointForSpacer: func,
  onTouchPointForFiller: func,
  handleQuickView: func.isRequired,
  handleDuplicate: func.isRequired,
  designId: string.isRequired,
  onRender: func,
  showDimensions: bool,
  totalWidth: number.isRequired,
  onEditCabinetAnalytics: func.isRequired,
  disableTouchPoints: bool,
  onFloorRectChange: func,
  onWallContainerRefChange: func,
  overlayPosition: shape({})
};
WallVisualizer.defaultProps = {
  cart: [],
  rows: 0,
  columns: 0,
  zoom: 1,
  onTouchPoint: () => { },
  onTouchPointForSpacer: () => { },
  onTouchPointForFiller: () => { },
  onRender: undefined,
  showDimensions: false,
  disableTouchPoints: false,
  onFloorRectChange: () => {},
  onWallContainerRefChange: () => {},
  overlayPosition: undefined
};
